import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../supabaseClient'

function ManageVariants() {
  const { lureId } = useParams()

  const [lure, setLure] = useState(null)
  const [existingVariants, setExistingVariants] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // New color entries to add
  const [newColorEntries, setNewColorEntries] = useState([{ colorName: '', isLimitedEdition: false, intendedUse: 'both' }])

  // Image upload state
  const [variantImages, setVariantImages] = useState({}) // { variantId: File }
  const [variantPreviews, setVariantPreviews] = useState({}) // { variantId: dataURL }

  useEffect(() => {
    fetchData()
  }, [lureId])

  const fetchData = async () => {
    try {
      const { data: lureData, error: lureError } = await supabase
        .from('base_lures')
        .select('*, brands (brand_id, brand_name)')
        .eq('base_lure_id', lureId)
        .single()

      if (lureError) throw lureError

      const { data: variantsData, error: variantsError } = await supabase
        .from('lure_variants')
        .select('*')
        .eq('base_lure_id', lureId)
        .order('color_name')

      if (variantsError) throw variantsError

      setLure(lureData)
      setExistingVariants(variantsData || [])
      setLoading(false)
    } catch (err) {
      console.error('Error fetching data:', err)
      setError('Failed to load data')
      setLoading(false)
    }
  }

  // ---- New color entry management ----

  const addColorEntry = () => {
    setNewColorEntries(prev => [...prev, { colorName: '', isLimitedEdition: false, intendedUse: 'both' }])
  }

  const removeColorEntry = (index) => {
    setNewColorEntries(prev => prev.filter((_, i) => i !== index))
  }

  const updateColorEntry = (index, field, value) => {
    setNewColorEntries(prev => prev.map((entry, i) =>
      i === index ? { ...entry, [field]: value } : entry
    ))
  }

  const handleAddVariants = async () => {
    const validEntries = newColorEntries.filter(e => e.colorName.trim())
    if (validEntries.length === 0) {
      setError('Please enter at least one color name')
      return
    }

    setError('')
    setSuccess('')

    try {
      const variants = validEntries.map(entry => ({
        base_lure_id: parseInt(lureId),
        color_name: entry.colorName.trim(),
        is_limited_edition: entry.isLimitedEdition,
        intended_use: entry.intendedUse || 'both'
      }))

      const { error: insertError } = await supabase
        .from('lure_variants')
        .insert(variants)

      if (insertError) throw insertError

      setSuccess(`Added ${validEntries.length} new color variant${validEntries.length !== 1 ? 's' : ''}!`)
      setNewColorEntries([{ colorName: '', isLimitedEdition: false, intendedUse: 'both' }])
      await fetchData()
      setTimeout(() => setSuccess(''), 3000)

    } catch (err) {
      console.error('Error adding variants:', err)
      if (err.code === '23505') {
        setError('One or more of these colors already exists for this lure.')
      } else {
        setError(err.message || 'Failed to add variants')
      }
    }
  }

  // ---- Delete variant ----

  const handleDeleteVariant = async (variant) => {
    if (!window.confirm(`Remove "${variant.variant_name || variant.color_name}" from ${lure.name}?`)) {
      return
    }

    try {
      const { error: deleteError } = await supabase
        .from('lure_variants')
        .delete()
        .eq('variant_id', variant.variant_id)

      if (deleteError) throw deleteError

      setSuccess(`Removed "${variant.variant_name || variant.color_name}"`)
      await fetchData()
      setTimeout(() => setSuccess(''), 3000)

    } catch (err) {
      console.error('Error deleting variant:', err)
      setError(err.message || 'Failed to delete variant')
    }
  }

  // ---- Image upload helpers ----

  const handleVariantImageSelect = (variantId, e) => {
    const file = e.target.files[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB')
      return
    }

    setVariantImages(prev => ({ ...prev, [variantId]: file }))

    const reader = new FileReader()
    reader.onloadend = () => {
      setVariantPreviews(prev => ({ ...prev, [variantId]: reader.result }))
    }
    reader.readAsDataURL(file)
    setError('')
  }

  const handleRemoveVariantPreview = (variantId) => {
    setVariantImages(prev => { const s = { ...prev }; delete s[variantId]; return s })
    setVariantPreviews(prev => { const s = { ...prev }; delete s[variantId]; return s })
  }

  const uploadVariantImage = async (variantId, file) => {
    const fileExt = file.name.split('.').pop()
    const fileName = `variants/${lureId}-${variantId}-${Date.now()}.${fileExt}`

    const { error: uploadError } = await supabase.storage
      .from('lure-images')
      .upload(fileName, file)

    if (uploadError) throw uploadError

    const { data: publicURL } = supabase.storage
      .from('lure-images')
      .getPublicUrl(fileName)

    return publicURL.publicUrl
  }

  const handleSaveVariantImage = async (variantId) => {
    const file = variantImages[variantId]
    if (!file) return

    try {
      const imageUrl = await uploadVariantImage(variantId, file)

      const { error: updateError } = await supabase
        .from('lure_variants')
        .update({ image_url: imageUrl })
        .eq('variant_id', variantId)

      if (updateError) throw updateError

      setSuccess('Image uploaded successfully!')
      handleRemoveVariantPreview(variantId)
      await fetchData()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      console.error('Error saving variant image:', err)
      setError('Failed to upload image')
    }
  }

  if (loading) {
    return <div className="loading">Loading...</div>
  }

  if (!lure) {
    return <div className="error-message">Lure not found</div>
  }

  return (
    <div className="admin-form-page">
      <h1>Manage Color Variants</h1>
      <div className="lure-info-banner">
        <strong>{lure.name}</strong> by {lure.brands?.brand_name}
      </div>

      {error && <div className="error-message">❌ {error}</div>}
      {success && <div className="success-message">✅ {success}</div>}

      {/* Existing Variants */}
      <div className="variants-section">
        <h2>Current Colors ({existingVariants.length})</h2>

        {existingVariants.length === 0 ? (
          <div className="no-variants-message">
            <p>No color variants yet. Add some below!</p>
          </div>
        ) : (
          <div className="variants-list-with-images">
            {existingVariants.map(variant => (
              <div key={variant.variant_id} className="variant-with-image-card">
                <div className="variant-main-info">
                  <div className="variant-info">
                    <span className="variant-color-name">
                      {variant.variant_name || variant.color_name}
                    </span>
                    {variant.color_name !== variant.variant_name && variant.variant_name && (
                      <span style={{ color: '#94a3b8', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                        ({variant.color_name})
                      </span>
                    )}
                    {variant.is_limited_edition && (
                      <span className="limited-badge-tiny">LE</span>
                    )}
                    {variant.intended_use && variant.intended_use !== 'both' && (
                      <span className={`intended-use-badge ${variant.intended_use === 'collection' ? 'badge-collectible-only' : 'badge-fishing-only'}`} style={{ fontSize: '0.65rem' }}>
                        {variant.intended_use === 'collection' ? 'Collectible' : 'Fishing Only'}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeleteVariant(variant)}
                    className="btn-delete-small"
                  >
                    🗑️
                  </button>
                </div>

                {/* Image Upload */}
                <div className="variant-image-section">
                  {variant.image_url && !variantPreviews[variant.variant_id] ? (
                    <div className="variant-current-image">
                      <img src={variant.image_url} alt={variant.color_name} className="variant-thumbnail" />
                      <span className="image-status">✓ Has image</span>
                    </div>
                  ) : variantPreviews[variant.variant_id] ? (
                    <div className="variant-image-preview">
                      <img src={variantPreviews[variant.variant_id]} alt="Preview" className="variant-thumbnail" />
                      <button
                        type="button"
                        onClick={() => handleRemoveVariantPreview(variant.variant_id)}
                        className="btn-remove-preview"
                      >
                        ✕
                      </button>
                      <button
                        onClick={() => handleSaveVariantImage(variant.variant_id)}
                        className="btn-save-image"
                      >
                        💾 Save
                      </button>
                    </div>
                  ) : (
                    <div className="variant-no-image">
                      <input
                        type="file"
                        id={`variant-image-${variant.variant_id}`}
                        accept="image/*"
                        onChange={(e) => handleVariantImageSelect(variant.variant_id, e)}
                        className="file-input-small"
                      />
                      <label htmlFor={`variant-image-${variant.variant_id}`} className="upload-label">
                        📷 Add Image
                      </label>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add New Colors */}
      <div className="variants-section">
        <h2>Add New Colors</h2>
        <p style={{ color: '#475569', marginBottom: '1rem' }}>
          Enter each color exactly as the manufacturer prints it.
        </p>

        <div className="color-entries-list">
          {newColorEntries.map((entry, index) => (
            <div key={index} className="color-entry-row">
              <div className="color-entry-inputs">
                <input
                  type="text"
                  value={entry.colorName}
                  onChange={(e) => updateColorEntry(index, 'colorName', e.target.value)}
                  placeholder={`Color name (e.g., Sexy Shad)`}
                  className="form-input"
                />
                <label className="checkbox-label-admin" style={{ whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={entry.isLimitedEdition}
                    onChange={(e) => updateColorEntry(index, 'isLimitedEdition', e.target.checked)}
                  />
                  <span>Limited Edition</span>
                </label>
                <select
                  value={entry.intendedUse || 'both'}
                  onChange={(e) => updateColorEntry(index, 'intendedUse', e.target.value)}
                  className="form-select"
                  style={{ minWidth: '140px' }}
                >
                  <option value="both">Fishing & Collectible</option>
                  <option value="collection">Collectible Only</option>
                  <option value="fishing">Fishing Only</option>
                </select>
                {newColorEntries.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeColorEntry(index)}
                    className="btn-delete-small"
                  >
                    🗑️
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <button type="button" onClick={addColorEntry} className="btn-secondary" style={{ marginTop: '0.5rem' }}>
          + Add Another Color
        </button>

        <div className="form-actions" style={{ marginTop: '1.5rem' }}>
          <Link to="/admin/manage-lures" className="btn-secondary">Done</Link>
          <button
            onClick={handleAddVariants}
            className="btn-primary"
            disabled={newColorEntries.every(e => !e.colorName.trim())}
          >
            Add {newColorEntries.filter(e => e.colorName.trim()).length} Color{newColorEntries.filter(e => e.colorName.trim()).length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ManageVariants
